import React, { PureComponent } from 'react'
import * as PIXI from 'pixi.js-legacy'
import PropTypes from 'prop-types'
import * as TWEEN from 'tween.js'

import { isTouch } from 'utils'
import app from 'common/builder/components/chart/FlowChart/app'
import { STATES, MIN_DRAG_DELTA } from 'common/builder/components/chart/FlowChart/config'
import Portal from 'common/builder/components/chart/FlowChart/nodes/shared/portals/Portal'
import { Colors } from 'common/builder/components/chart/FlowChart/nodes/shared/styles'

const resolution = window.devicePixelRatio

const MAX_SCALE = 1.7

const getDotURL = (radius, border, background) => {
  background = encodeURIComponent(background)
  border = encodeURIComponent(border)
  const size = radius * 2 * resolution
  return `data:image/svg+xml;charset=utf-8,%3Csvg width='${size}' height='${size}' viewBox='0 0 22 22' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='11' cy='11' r='9.5' fill='${background}' stroke='${border}' stroke-width='3'/%3E%3C/svg%3E%0A`
}

const getTexture = (radius, border, background) => {
  return PIXI.Texture.from(getDotURL(radius, border, background), { resolution })
}

export default class StartDot extends PureComponent {
  static propTypes = {
    color: PropTypes.string.isRequired,
    hasTarget: PropTypes.bool,
    isHover: PropTypes.bool,
    offsetX: PropTypes.number,
    onClick: PropTypes.func.isRequired,
    onDragEnd: PropTypes.func.isRequired,
    onDragStart: PropTypes.func.isRequired,
    onMouseOver: PropTypes.func,
    onMouseOut: PropTypes.func,
    position: PropTypes.object.isRequired,
    radius: PropTypes.number.isRequired,
    readonly: PropTypes.bool,
    disabled: PropTypes.bool,
  }

  static defaultProps = {
    readonly: false,
  }

  hitArea = new PIXI.Circle(0, 0, this.props.radius * MAX_SCALE * (isTouch ? 2.5 : 1))

  state = {
    isMouseDown: false,
    isDragging: false,
  }

  tweenData = { scale: 1 }

  componentDidMount() {
    this.tween = new TWEEN.Tween(this.tweenData).onUpdate(this.handleTweenUpdate)
    this.props.position.on('update', this.update)
  }

  componentDidUpdate(prevProps, prevState) {
    this.update()
  }

  componentWillUnmount() {
    this.tween.stop()
    this.props.position.removeListener('update', this.update)
  }

  update = () => {
    const { x1, y1, width } = this.props.position
    if (this.container) {
      this.container.position.set(x1, y1)
    }
    if (this.offset) {
      this.offset.height = width
    }
  }

  scale = (value = 1, duration = 150) => {
    this.tween.stop().to({ scale: value }, duration).start()
  }

  handleClick = (e) => {
    if (app.state !== STATES.NORMAL || this.props.disabled) {
      return
    }
    this.scale(1, 0)
    !this.props.disabled && this.props.onClick(e)
  }

  handleMouseDown = (e) => {
    if (app.state !== STATES.NORMAL || this.props.disabled) {
      return
    }
    // Preventing browser selection
    const { originalEvent } = e.data
    originalEvent.preventDefault && originalEvent.preventDefault()

    app.blockPanning()
    this.setState({ isMouseDown: true })
  }

  handleMouseMove = (e) => {
    if (!this.state.isMouseDown || this.state.isDragging) {
      return
    }
    if (e.data.mouseDragDelta <= MIN_DRAG_DELTA) {
      return
    }
    this.setState({ isDragging: true })
    this.props.onDragStart(e)
  }

  handleMouseUp = (e) => {
    if (this.state.isDragging) {
      setTimeout(() => this.props.onDragEnd())
    }
    app.unblockPanning()
    this.setState({ isMouseDown: false, isDragging: false })
  }

  handleMouseOver = (e) => {
    this.props.onMouseOver && this.props.onMouseOver(e)
    if (app.state !== STATES.NORMAL || this.props.disabled) {
      return
    }

    this.scale(MAX_SCALE)
  }

  handleMouseOut = (e) => {
    this.props.onMouseOut && this.props.onMouseOut(e)
    if (this.props.disabled) return

    this.scale(1)
  }

  handleTweenUpdate = () => {
    if (!this.dot) {
      return
    }
    const { scale } = this.tweenData
    this.dot.scale.set(scale, scale)
    app.render()
  }

  handleOffsetRef = (el) => {
    this.offset = el
    if (el) {
      this.update()
    }
  }

  handleDotRef = (el) => {
    this.dot = el
  }

  handleContainerRef = (el) => {
    this.container = el
    if (el) {
      this.update()
    }
  }

  render() {
    const { color, position, hasTarget, isHover, offsetX, radius, readonly } = this.props
    const textureBorderColor = isHover ? Colors.PRIMARY : color
    const textureBackgroundColor = hasTarget ? textureBorderColor : Colors.WHITE
    const texture = getTexture(radius, textureBorderColor, textureBackgroundColor)

    const handlers = readonly ? {} : this

    return (
      <Portal to="foreground">
        <container
          ref={this.handleContainerRef}
          hitArea={this.hitArea}
          onClick={handlers.handleClick}
          onMouseDown={handlers.handleMouseDown}
          onMouseUp={handlers.handleMouseUp}
          onMouseUpOutside={handlers.handleMouseUp}
          onMouseOver={handlers.handleMouseOver}
          onMouseOut={handlers.handleMouseOut}
          onMouseMove={handlers.handleMouseMove}
          onTouchStart={handlers.handleMouseDown}
          onTouchEnd={handlers.handleMouseUp}
          onTouchEndOutside={handlers.handleMouseUp}
          onTouchMove={handlers.handleMouseMove}
        >
          {hasTarget && offsetX !== 0 && (
            <sprite
              ref={this.handleOffsetRef}
              texture={PIXI.Texture.WHITE}
              tint={Number((isHover ? Colors.PRIMARY : color).replace('#', '0x'))}
              width={offsetX}
              height={position.width}
              alpha={isHover ? 1 : 0.2}
              anchor={[0, 0.5]}
            />
          )}
          <sprite ref={this.handleDotRef} texture={texture} anchor={[0.5, 0.5]} />
        </container>
      </Portal>
    )
  }
}
